import { GeneralError } from '@common/components/general-error/general-error';
import { Redirect } from '@common/components/redirect';
import { useLang } from '@common/context/language-provider';
import { useSpaceContext } from '@common/context/space';
import { SurveyFilterProvider } from '@common/context/survey/survey-filter-provider';
import { UserContext } from '@common/context/user';
import { useLegalModalStore } from '@common/stores';
import { IFooterProps, ISurveyCategory, ISurveyStage } from '@konova/api-types';
import { LoadingIndicator } from '@konova/ui/loading-indicator';
import { SpaceAdminRoutes } from '@routes/space-admin-routes';
import { SpaceRoutes } from '@routes/space-routes';
import { FC, lazy, Suspense, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { generatePath, Route, Routes, useLocation, useNavigate, useParams } from 'react-router';
import { FALLBACK_LANGUAGE, FALLBACK_PRIVACY_PDF_URL } from 'src/constants';
import { ISurvey } from 'src/types';

const SurveyNotFound = lazy(() => import('src/pages/SurveyNotFound'));
const SpaceAdminMaster = lazy(() => import('src/components/RouteMasters/SpaceAdminMaster'));
const PasswordReset = lazy(() => import('src/pages/PasswordReset'));
const Support = lazy(() => import('src/pages/Support'));
const SurveyMaster = lazy(() => import('src/components/RouteMasters/SurveyMaster'));
const TwoFALogin = lazy(() => import('../../pages/Login/TwoFALogin'));
const TwoFaSetup = lazy(() => import('../../pages/Login/TwoFASetup'));
const SpaceOverview = lazy(() => import('src/pages/Space/Overview'));
const Information = lazy(() => import('src/pages/Space/Information'));
const NotFoundPage = lazy(() => import('src/pages/NotFoundPage'));
const AccessibilityPage = lazy(() => import('@space/pages/accessibility-page/accessibility-page'));
const GeneralReportDownload = lazy(() => import('../../pages/GeneralReportDownload'));

interface ISpaceMaster {
  survey: ISurvey | null;
  setSurvey: (survey: ISurvey | null) => void;
  setIsSentryEnabled: (sentryState: boolean) => void;
  setEmailVerificationActivated: (state: boolean) => void;
  setTwoFaActivated: (state: boolean) => void;
  setLoginUri: (uri: string) => void;
  footerProps: IFooterProps;
  setFooterProps: (props: IFooterProps) => void;
  setLanguages: (languages: string[]) => void;
  activeSurveyStage?: ISurveyStage | null;
  setActiveSurveyStage: (stage?: ISurveyStage | null) => void;
  setContextCategories: (categories: ISurveyCategory[]) => void;
  setOnNavbarBrandClick: (handler?: () => void) => void;
}

const SpaceMaster: FC<ISpaceMaster> = (
  {
    setLoginUri,
    footerProps,
    setFooterProps,
    survey,
    setIsSentryEnabled,
    setEmailVerificationActivated,
    setTwoFaActivated,
    setLanguages,
    activeSurveyStage,
    setActiveSurveyStage,
    setSurvey,
    setContextCategories,
    setOnNavbarBrandClick,
  }
) => {
  const { language: lang } = useLang();
  const params = useParams<{ lang?: string }>();
  const { space, isLoading: spaceLoading } = useSpaceContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const pathSegments = location.pathname.split('/').filter(segment => !!segment);
  const [isCustomStylesSet, setIsCustomStylesSet] = useState<boolean>(false);
  const setLegalPdfUrl = useLegalModalStore(state => state.setPdfUrl);

  useEffect(() => {
    if (pathSegments.length < 2) {
      return;
    }

    if (!location.pathname.includes('admin')) {
      return;
    }

    setLoginUri(`/${lang}/${pathSegments[1]}/login`);
  }, [location.pathname, pathSegments, lang]);

  useEffect(() => {
    setIsSentryEnabled(!!space?.module_sentry_is_activated);
    setEmailVerificationActivated(!!space?.module_email_verification_is_activated);
    setTwoFaActivated(!!space?.two_fa_is_activated);
  }, [space]);

  useEffect(() => {
    setLanguages(space?.languages || ['de']);

    if (space?.languages && !params.lang) {
      navigate({
        pathname: `/${space.languages[0] ?? FALLBACK_LANGUAGE}`,
        search: location.search,
        hash: location.hash
      });
    }

  }, [space]);

  useEffect(() => {
    setTimeout(() => {
      if (!isCustomStylesSet && space?.custom_styles) {
        const styles = document.getElementById('custom-css');
        if (styles) {
          styles.innerHTML = space.custom_styles;
          document.body.insertBefore(styles, document.body.firstChild);
        }
        setIsCustomStylesSet(true);
      }
    }, 0);
  }, [space]);

  useEffect(() => {
    if (space && !survey) {
      setFooterProps({
        organizationData: {
          ...space,
          organization: space.name,
          termsOfUsePdfUrl: space.termsOfUsePdfUrl || FALLBACK_PRIVACY_PDF_URL
        }
      });
      setLegalPdfUrl(space.termsOfUsePdfUrl ?? null);
    }
  }, [space, survey]);


  if (space?.languages && !space.languages.includes(lang!)) {
    const userSavedLanguage = localStorage.getItem('userLanguage');

    let newLocationPathname = location.pathname;
    const splitLocation = location.pathname.split('/');

    if (splitLocation[1].length === 2) {
      delete splitLocation[0];
      delete splitLocation[1];
      newLocationPathname = splitLocation.filter(n => n).join('/');
    } else {
      newLocationPathname = newLocationPathname.substring(1);
    }

    if (userSavedLanguage && space.languages.includes(userSavedLanguage)) {
      window.location.href = `/${userSavedLanguage}/${newLocationPathname}${location.search}`;
    } else {
      window.location.href = `/${space.languages[0]}/${newLocationPathname}${location.search}`;
    }
    return null;
  }


  if (!space && spaceLoading) return null;

  if (!space) {
    return <NotFoundPage />;
  }

  return (
    <Routes>
      <Route
        index
        element={<Suspense fallback={<LoadingIndicator />}>
          <ErrorBoundary fallbackRender={({ error }) => <GeneralError error={error as Error} />}>
            <SurveyFilterProvider>
              <SpaceOverview />
            </SurveyFilterProvider>
          </ErrorBoundary>
        </Suspense>}
      />

      <Route
        path={'survey-not-found'}
        element={<Suspense fallback={<LoadingIndicator />}>
          <ErrorBoundary fallbackRender={({ error }) => <GeneralError error={error as Error} />}>
            <SurveyNotFound />
          </ErrorBoundary>
        </Suspense>}
      />

      <Route
        path={'support'}
        element={<Suspense fallback={<LoadingIndicator />}>
          <ErrorBoundary fallbackRender={({ error }) => <GeneralError error={error as Error} />}>
            <Support context={'space'} />
          </ErrorBoundary>
        </Suspense>}
      />

      <Route
        path={'/surveys/general-report-download'}
        element={<Suspense fallback={<LoadingIndicator />}>
          <ErrorBoundary fallbackRender={({ error }) => <GeneralError error={error as Error} />}>
            <GeneralReportDownload />
          </ErrorBoundary>
        </Suspense>
        }
      />

      <Route
        path={'2fa-login'}
        element={<Suspense fallback={<LoadingIndicator />}>
          <ErrorBoundary fallbackRender={({ error }) => <GeneralError error={error as Error} />}>
            <TwoFALogin
              headerOrgName={space?.name ?? null}
              footerProps={footerProps} />
          </ErrorBoundary>
        </Suspense>}
      />


      <Route
        path={'2fa-setup'}
        element={<UserContext.Consumer>
          {({ user }) => <>
            {user ? <Redirect to={generatePath(SpaceRoutes.index, { lang: lang ?? '' })} /> : <TwoFaSetup
              headerTitle={space?.name ?? ''}
              footerProps={footerProps}
            />}
          </>}
        </UserContext.Consumer>
        } />

      <Route
        path={'reset-pw/:token/:email/:role'}
        element={<Suspense fallback={<LoadingIndicator />}>
          <ErrorBoundary fallbackRender={({ error }) => <GeneralError error={error as Error} />}>
            <PasswordReset
              loginUrl={generatePath(SpaceAdminRoutes.login, { lang: lang! })}
              footerProps={footerProps} />
          </ErrorBoundary>
        </Suspense>
        } />

      <Route
        path={'information'}
        element={<Suspense fallback={<LoadingIndicator />}>
          <ErrorBoundary fallbackRender={({ error }) => <GeneralError error={error as Error} />}>
            <Information />
          </ErrorBoundary>
        </Suspense>}
      />

      <Route
        path={'accessibility'}
        element={<Suspense fallback={<LoadingIndicator />}>
          <ErrorBoundary fallbackRender={({ error }) => <GeneralError error={error as Error} />}>
            <AccessibilityPage />
          </ErrorBoundary>
        </Suspense>}

      />

      <Route
        path='admin'>
        <Route
          index
          path="*"
          element={<Suspense fallback={<LoadingIndicator />}>
            <ErrorBoundary fallbackRender={({ error }) => <GeneralError error={error as Error} />}>
              <SpaceAdminMaster
                survey={survey}
                activeSurveyStage={activeSurveyStage}
                footerProps={footerProps}
                setContextCategories={setContextCategories}
              />
            </ErrorBoundary>
          </Suspense>}
        />
      </Route>


      <Route path=':surveySlug'>
        <Route
          index
          path="*"
          element={<Suspense fallback={<LoadingIndicator />}>
            <ErrorBoundary fallbackRender={({ error }) => <GeneralError error={error as Error} />}>
              <SurveyMaster
                footerProps={footerProps}
                setFooterProps={setFooterProps}
                survey={survey}
                setSurvey={setSurvey}
                setActiveStage={setActiveSurveyStage}
                activeSurveyStage={activeSurveyStage}
                setLoginUri={setLoginUri}
                setOnNavbarBrandClick={setOnNavbarBrandClick}
              />
            </ErrorBoundary>
          </Suspense>}
        />
      </Route>
    </Routes>
  );
};

export default SpaceMaster;